if (!customElements.get('product-enquiry-drawer')) {
  customElements.define(
    'product-enquiry-drawer',
    class ProductEnquiryDrawer extends HTMLElement {
      constructor() {
        super();

        this.trigger = this.querySelector('.product-enquiry-drawer__trigger');
        this.content = this.querySelector('[data-product-enquiry-content]');
        this.panel = this.querySelector('.product-enquiry-drawer__panel');
        this.formHost = this.querySelector('[data-product-enquiry-form]');
        this.closeButtons = this.querySelectorAll('[data-product-enquiry-close]');
        this.formfulDialogObserver = null;
        this.formfulPanelTimer = null;
        this.openedFormfulDialog = false;
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onFormfulDocumentClick = this.onFormfulDocumentClick.bind(this);
        this.onFormfulDocumentPointerDown = this.onFormfulDocumentPointerDown.bind(this);
        this.onTransitionEnd = this.onTransitionEnd.bind(this);
      }

      connectedCallback() {
        this.trigger?.addEventListener('click', () => this.open());
        this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
        this.addEventListener('shopify:block:select', () => this.open());
        this.addEventListener('shopify:block:deselect', () => this.close(false));
        if (this.querySelector('.form-status')) this.open();
      }

      disconnectedCallback() {
        this.stopFormfulDialogTracking();
      }

      hasRenderableForm(host) {
        return host.querySelector('form, iframe, .formful, [class*="formful"]') !== null;
      }

      openFormfulDialog() {
        const formId = this.dataset.formId;
        if (!formId || typeof window.Formful?.openDialog !== 'function') return false;

        try {
          window.Formful.openDialog(formId);
          this.openedFormfulDialog = true;
          this.startFormfulDialogTracking();
          return true;
        } catch (error) {
          console.warn('Unable to open Formful dialog.', error);
          return false;
        }
      }

      closeFormfulDialog() {
        const formId = this.dataset.formId;
        if (!this.openedFormfulDialog || !formId || typeof window.Formful?.closeDialog !== 'function') return false;

        try {
          window.Formful.closeDialog(formId);
          this.openedFormfulDialog = false;
          this.stopFormfulDialogTracking();
          return true;
        } catch (error) {
          console.warn('Unable to close Formful dialog.', error);
          return false;
        }
      }

      startFormfulDialogTracking() {
        document.body.classList.add('product-enquiry-formful--open');
        document.addEventListener('pointerdown', this.onFormfulDocumentPointerDown, true);
        document.addEventListener('click', this.onFormfulDocumentClick, true);
        document.addEventListener('keyup', this.onKeyUp);
        this.scheduleFormfulPanelClass();

        this.formfulDialogObserver?.disconnect();
        this.formfulDialogObserver = new MutationObserver(() => {
          if (!this.openedFormfulDialog) return;

          window.requestAnimationFrame(() => {
            if (!this.hasVisibleFormfulDialog()) this.stopFormfulDialogTracking();
            this.applyFormfulPanelClass();
          });
        });
        this.formfulDialogObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'aria-hidden'] });
      }

      stopFormfulDialogTracking() {
        this.openedFormfulDialog = false;
        document.body.classList.remove('product-enquiry-formful--open');
        document.removeEventListener('pointerdown', this.onFormfulDocumentPointerDown, true);
        document.removeEventListener('click', this.onFormfulDocumentClick, true);
        document.removeEventListener('keyup', this.onKeyUp);
        window.clearTimeout(this.formfulPanelTimer);
        document.querySelectorAll('.product-enquiry-formful-modal').forEach((element) => {
          element.classList.remove('product-enquiry-formful-modal');
        });
        this.formfulDialogObserver?.disconnect();
        this.formfulPanelTimer = null;
        this.formfulDialogObserver = null;
      }

      hasVisibleFormfulDialog() {
        const candidates = document.querySelectorAll('[class*="formful"], [id*="formful"]');
        return Array.from(candidates).some((element) => {
          if (element.closest('product-enquiry-drawer')) return false;

          const rect = element.getBoundingClientRect();
          const style = window.getComputedStyle(element);
          return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none' && element.getAttribute('aria-hidden') !== 'true';
        });
      }

      getVisibleFormfulDialog() {
        const candidates = document.querySelectorAll('[class*="formful"], [id*="formful"]');
        return Array.from(candidates)
          .filter((element) => {
            if (element.closest('product-enquiry-drawer')) return false;
            if (!element.querySelector('input, select, textarea, form')) return false;

            const rect = element.getBoundingClientRect();
            const style = window.getComputedStyle(element);
            return rect.width > 280 && rect.height > 220 && style.visibility !== 'hidden' && style.display !== 'none' && element.getAttribute('aria-hidden') !== 'true';
          })
          .map((element) => {
            const rect = element.getBoundingClientRect();
            const style = window.getComputedStyle(element);
            const formfulMatch = /formful/i.test(`${element.id} ${element.className}`);
            const dialogMatch = element.matches('[role="dialog"], dialog, [class*="modal"]');
            const hasFields = element.querySelector('input, select, textarea, form');
            const hasClose = Array.from(element.querySelectorAll('button, [role="button"], a')).some((button) => {
              const label = `${button.getAttribute('aria-label') || ''} ${button.textContent || ''} ${button.className || ''}`.toLowerCase();
              return label.includes('close') || label.includes('×') || label.trim() === 'x';
            });
            const fixedLayer = style.position === 'fixed' || style.position === 'absolute';

            return {
              element,
              score:
                (formfulMatch ? 100 : 0) +
                (dialogMatch ? 50 : 0) +
                (hasFields ? 40 : 0) +
                (hasClose ? 20 : 0) +
                (fixedLayer ? 15 : 0) +
                Math.min((rect.width * rect.height) / 100000, 20),
            };
          })
          .sort((a, b) => b.score - a.score)[0]?.element;
      }

      scheduleFormfulPanelClass(attempt = 0) {
        window.clearTimeout(this.formfulPanelTimer);
        this.formfulPanelTimer = window.setTimeout(() => {
          if (this.applyFormfulPanelClass() || attempt >= 20) return;
          this.scheduleFormfulPanelClass(attempt + 1);
        }, attempt === 0 ? 50 : 100);
      }

      applyFormfulPanelClass() {
        const dialog = this.getVisibleFormfulDialog();
        if (!dialog) return false;

        dialog.classList.add('product-enquiry-formful-modal');
        return true;
      }

      onFormfulDocumentPointerDown(event) {
        const dialog = this.getVisibleFormfulDialog();
        if (dialog && !dialog.contains(event.target)) {
          window.setTimeout(() => {
            this.stopFormfulDialogTracking();
          }, 150);
          return;
        }
      }

      onFormfulDocumentClick(event) {
        const dialog = this.getVisibleFormfulDialog();
        if (dialog && !dialog.contains(event.target)) {
          window.setTimeout(() => {
            this.stopFormfulDialogTracking();
          }, 150);
          return;
        }

        const closeTrigger = event.target.closest('button, [role="button"], a');
        if (!closeTrigger) return;

        const label = `${closeTrigger.getAttribute('aria-label') || ''} ${closeTrigger.textContent || ''} ${closeTrigger.className || ''}`.toLowerCase();
        if (!label.includes('close') && !label.includes('×') && label.trim() !== 'x') return;

        window.setTimeout(() => {
          this.stopFormfulDialogTracking();
        }, 150);
      }

      open() {
        if (this.classList.contains('is-open')) return;
        if (this.openFormfulDialog()) {
          window.setTimeout(() => {
            if (!this.hasVisibleFormfulDialog()) this.openNativeDrawer();
          }, 300);
          return;
        }

        this.openNativeDrawer();
      }

      openNativeDrawer() {
        if (this.classList.contains('is-open')) return;
        if (!this.content || !this.panel) return;

        if (typeof setScrollbarWidth === 'function') setScrollbarWidth();

        this.content.hidden = false;
        document.body.classList.add('product-enquiry-drawer--open');
        document.addEventListener('keyup', this.onKeyUp);
        this.trigger?.setAttribute('aria-expanded', 'true');

        window.requestAnimationFrame(() => {
          this.classList.add('is-open');
          if (typeof trapFocus === 'function') {
            trapFocus(
              this.panel,
              this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || this.panel
            );
          } else {
            this.panel.focus();
          }
        });
      }

      close(returnFocus = true) {
        if (this.closeFormfulDialog()) {
          if (returnFocus) this.trigger?.focus();
          return;
        }

        if (!this.content || !this.classList.contains('is-open')) return;

        this.classList.remove('is-open');
        document.body.classList.remove('product-enquiry-drawer--open');
        document.removeEventListener('keyup', this.onKeyUp);
        this.trigger?.setAttribute('aria-expanded', 'false');

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(returnFocus ? this.trigger : null);
        } else if (returnFocus) {
          this.trigger?.focus();
        }

        this.panel?.addEventListener('transitionend', this.onTransitionEnd, { once: true });
      }

      onTransitionEnd(event) {
        if (event.target !== this.panel || this.classList.contains('is-open')) return;
        this.content.hidden = true;
      }

      onKeyUp(event) {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      }
    }
  );
}

if (!window.__mooresEnquiryOpenBound) {
  window.__mooresEnquiryOpenBound = true;
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-product-enquiry-open]');
    if (!opener) return;

    const drawer = document.querySelector('product-enquiry-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;

    event.preventDefault();
    drawer.open();
  });
}
